/**
 * CardPickerDialog Component - Add KPI cards to dashboard grid
 *
 * v2.96 - 2026-02-07 - Initial implementation
 * - Shows available Gold tables not yet on dashboard
 * - User selects table, card is created via dashboardKPIService
 * - Card appears in grid at next available position
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  CircularProgress,
  Alert,
  Checkbox,
  Box,
} from '@mui/material';
import { BarChart as BarChartIcon, Add as AddIcon } from '@mui/icons-material';
import { dashboardKPIService } from '../services/api';

interface CardPickerDialogProps {
  open: boolean;
  onClose: () => void;
  onCardsAdded: () => void;
  existingCardIds: string[];
}

// v4.16 - DASHBOARD-EDIT-VERIFY-01: cards offered by the picker are those NOT
// already on the dashboard. Pure + exported so it is unit-testable without a DOM.
export function filterAddableCards<T extends { cardId: string }>(
  allCards: T[],
  existingCardIds: string[],
): T[] {
  const present = new Set(existingCardIds);
  return (allCards || []).filter((c) => !present.has(c.cardId));
}

const CardPickerDialog: React.FC<CardPickerDialogProps> = ({
  open,
  onClose,
  onCardsAdded,
  existingCardIds,
}) => {
  const [availableTables, setAvailableTables] = useState<any[]>([]);
  const [selectedTables, setSelectedTables] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // v2.96 - Fetch available cards that are not on the dashboard
  useEffect(() => {
    if (!open) return;

    const fetchAvailable = async () => {
      setLoading(true);
      setError(null);
      try {
        // Get all cards (including hidden ones)
        const cardsRes = await dashboardKPIService.getCards();
        if (cardsRes.success) {
          // Filter out cards already on the dashboard. v4.16: extracted to a pure
          // helper so the exclusion is unit-testable (the Dashboard now feeds real
          // existingCardIds instead of the old hardcoded []).
          setAvailableTables(filterAddableCards(cardsRes.cards || [], existingCardIds));
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load available cards');
      } finally {
        setLoading(false);
      }
    };

    fetchAvailable();
    setSelectedTables([]);
  }, [open, existingCardIds]);

  const handleToggle = (cardId: string) => {
    setSelectedTables((prev) =>
      prev.includes(cardId) ? prev.filter((id) => id !== cardId) : [...prev, cardId]
    );
  };

  const handleAdd = async () => {
    if (selectedTables.length === 0) return;
    setAdding(true);
    setError(null);

    try {
      // v2.96 - For now, the cards already exist as kpi_card docs.
      // We just need to update the dashboard config to unhide them.
      const configRes = await dashboardKPIService.getConfig();
      if (configRes.success) {
        const config = configRes.config;
        // Remove selected cards from hiddenCards
        const updatedHidden = (config.hiddenCards || []).filter(
          (id: string) => !selectedTables.includes(id)
        );
        await dashboardKPIService.updateConfig({
          ...config,
          hiddenCards: updatedHidden,
        });
      }

      onCardsAdded();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to add cards');
    } finally {
      setAdding(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add KPI Cards</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : availableTables.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            All available KPI cards are already on your dashboard.
          </Typography>
        ) : (
          <List>
            {availableTables.map((card: any) => (
              <ListItem key={card.cardId} disablePadding>
                <ListItemButton onClick={() => handleToggle(card.cardId)}>
                  <Checkbox
                    edge="start"
                    checked={selectedTables.includes(card.cardId)}
                    disableRipple
                  />
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <BarChartIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={card.title}
                    secondary={`${card.category} - ${card.goldTable}`}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          startIcon={adding ? <CircularProgress size={16} /> : <AddIcon />}
          onClick={handleAdd}
          disabled={selectedTables.length === 0 || adding}
        >
          Add {selectedTables.length > 0 ? `(${selectedTables.length})` : ''}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CardPickerDialog;
